import { GetMainMenuItemsParams, MenuItemDef, SortDirection } from '@ag-grid-community/core';

export interface ActiveServerSort {
  colId: string | null;
  dir: SortDirection;
}

export interface ColumnMenuOptions {
  /** The server sort in force, read at menu-open time so the toggle reflects live state. */
  activeSort: ActiveServerSort;
  /** Applies a backend sort. An empty direction clears it. */
  onServerSort: (colId: string, direction: 'Asc' | 'Desc' | '') => void;
  /** Restores default column state, including dropping any persisted pins. */
  onResetColumns: () => void;
}

/**
 * Builds a column's menu from AG Grid's own default items (Pin submenu, autosize,
 * separators), with two substitutions.
 *
 * Server-sorted columns are marked `sortable: false` so AG Grid never sorts them
 * client-side, which also means its default menu offers no sort items — this adds
 * Sort Ascending / Sort Descending entries that call the backend instead, and
 * re-picking the direction already in force clears the sort.
 *
 * `resetColumns` is replaced because the built-in item resets column state only and
 * cannot know about pins persisted outside the grid. `columnChooser` is dropped
 * because ColumnsToolPanel is not registered (see `src/lib/agGrid`).
 */
export function buildColumnMenuItems(params: GetMainMenuItemsParams, options: ColumnMenuOptions): (string | MenuItemDef)[] {
  const { activeSort, onServerSort, onResetColumns } = options;
  const defaults = (params.defaultItems ?? []).filter((item) => item !== 'columnChooser' && item !== 'resetColumns');
  const resetColumns: MenuItemDef = { name: 'Reset Columns', action: onResetColumns };

  const colId = params.column.getColId();
  const { serverSortable } = (params.column.getColDef().context ?? {}) as { serverSortable?: boolean };
  if (!serverSortable) return [...defaults, resetColumns];

  const isAsc = activeSort.colId === colId && activeSort.dir === 'asc';
  const isDesc = activeSort.colId === colId && activeSort.dir === 'desc';

  /* The Figma menu shows the asc/desc arrow glyph on each sort row, so the direction is
     supplied through `icon` and `checked` is left unset — AG Grid swaps a custom icon for
     a generic checkmark when a row is checked, which would hide the arrow. The active
     direction is instead reflected by the sort indicator in the column header. Re-picking
     the direction already in force clears the sort. */
  return [
    {
      name: 'Sort Ascending',
      icon: '<span class="ag-icon ag-icon-asc" role="presentation"></span>',
      action: () => onServerSort(colId, isAsc ? '' : 'Asc'),
    },
    {
      name: 'Sort Descending',
      icon: '<span class="ag-icon ag-icon-desc" role="presentation"></span>',
      action: () => onServerSort(colId, isDesc ? '' : 'Desc'),
    },
    'separator',
    ...defaults,
    resetColumns,
  ];
}
